package dev.lingvo.site.ds

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier

/**
 * The edge a sheet is anchored to, and therefore the direction it arrives from.
 *
 * Only the two this product has. A sheet anchored to an edge it does not touch is a popover, and
 * [Modifier.transitionSurface] with a few pixels of travel is the right treatment for one of those.
 */
enum class LSheetEdge {
    LEFT,
    BOTTOM,
    ;

    /**
     * Where the sheet sits when it is not on screen, as a percentage of **its own size**.
     *
     * A percentage rather than a pixel offset, so a 280px navigation panel and a full-width bottom
     * sheet are both exactly off screen without either of them naming a number that has to be kept
     * in step with its width.
     */
    internal val offscreen: String
        get() = when (this) {
            LEFT -> "translateX(-100%)"
            BOTTOM -> "translateY(100%)"
        }
}

/**
 * A panel that arrives from outside the screen, rather than from a few pixels away.
 *
 * **The mobile navigation drawer used `translateX(-6px)`.** Six pixels is the travel this system
 * gives a menu or a tooltip, and it is right for those: they belong to a trigger a few pixels away,
 * so a short move says where they came from. A drawer is anchored to the edge of the viewport and
 * covers the page — it did not come from six pixels away, it came from outside, and a six-pixel
 * nudge on a full-height panel reads as a flicker rather than as an arrival.
 *
 * The curve is the one Vaul ships and the animation playbook names for exactly this: it leaves
 * quickly and settles long, which is what makes a sheet feel like it has weight rather than being
 * faded in. [Motion.SlowMs] to enter, because this is an overlay covering content and travels the
 * full width of itself; [Motion.OverlayExitMs] to leave, keeping the asymmetry the rest of the
 * system uses — a dismissal should not make anybody wait.
 *
 * Transform and opacity only, and applied through the same `data-presence` attribute every other
 * surface in this product uses, so a sheet is interruptible the way a menu is: reopening one that is
 * still leaving retargets from where it has got to rather than restarting from off screen.
 *
 * Use it in three places on a `CssStyle`:
 * ```
 * base { Modifier.sheetSurface() }
 * cssRule("[data-presence=\"entering\"]") { Modifier.sheetHidden(LSheetEdge.LEFT) }
 * cssRule("[data-presence=\"exiting\"]") { Modifier.sheetLeaving(LSheetEdge.LEFT) }
 * ```
 */
fun Modifier.sheetSurface(): Modifier = styleModifier {
    property(
        "transition",
        "opacity ${Motion.SlowMs}ms ${Motion.EaseDrawer}, transform ${Motion.SlowMs}ms ${Motion.EaseDrawer}",
    )
}

/** Where the sheet starts, one frame before it is asked to arrive. */
fun Modifier.sheetHidden(edge: LSheetEdge): Modifier = styleModifier {
    property("transform", edge.offscreen)
}

/**
 * Where it goes, and how fast.
 *
 * `pointer-events: none` because a sheet on its way out still covers the page it is uncovering, and
 * a tap that lands on a leaving panel goes nowhere.
 */
fun Modifier.sheetLeaving(edge: LSheetEdge): Modifier = styleModifier {
    property("opacity", "0")
    property("transform", edge.offscreen)
    property("pointer-events", "none")
    property(
        "transition",
        "opacity ${Motion.OverlayExitMs}ms ${Motion.EaseIn}, " +
            "transform ${Motion.OverlayExitMs}ms ${Motion.EaseIn}",
    )
}
